import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollNavigation } from "@/contexts/ScrollContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import sphereImage from "@/assets/sphere.png";
import { ArrowDown, Calendar } from "lucide-react";

declare global {
  interface Window {
    Calendly: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

const HeroSection = () => {
  const { t } = useLanguage();
  const { goToSection, currentSection } = useScrollNavigation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const scrollToNextSection = () => {
    goToSection(currentSection + 1);
  };
  
  const handleExploreCourses = () => {
    navigate(user ? "/courses" : "/auth");
  };
  
  const handleBookCall = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: 'https://calendly.com/cepulisedgaras/30min' });
    }
  };
  
  return (
    <section className="min-h-screen flex items-center justify-center pt-20 px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-12 animate-reveal-up">
          <p className="text-sm md:text-base tracking-[0.3em] mb-8 text-muted-foreground">
            {t("hero.tagline")}
          </p>
          <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-black mb-8 md:mb-12 tracking-tight text-glow-hover uppercase">
            {t("hero.title")}
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 text-left animate-reveal-up" style={{ animationDelay: "0.2s" }}>
            <p className="text-lg md:text-xl leading-relaxed">
              {t("hero.freelancers")} <span className="text-holographic italic font-semibold">{t("hero.fragments")}</span>.
            </p>
            <p className="text-lg md:text-xl leading-relaxed">
              {t("hero.weDeliver")} <span className="text-holographic-reverse font-bold">{t("hero.completeStrategy")}</span>.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
              {t("hero.description")}{" "}
              <span className="text-holographic font-semibold">{t("hero.integratedPartner")}</span> {t("hero.cohesive")}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4 items-center">
              <Button size="lg" variant="holographic" className="font-bold w-full sm:w-auto min-h-[44px]" onClick={handleExploreCourses}>
                {t("hero.exploreCourses")}
              </Button>
              <Button size="lg" variant="outline" className="font-bold w-full sm:w-auto min-h-[44px] gap-2" onClick={handleBookCall}>
                <Calendar className="w-5 h-5" />
                {t("cta.bookCall")}
              </Button>
              <button
                onClick={scrollToNextSection}
                className="group relative w-14 h-14 rounded-full bg-gradient-to-br from-holographic-start via-holographic-middle to-holographic-end hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all duration-300 flex items-center justify-center animate-bounce hidden sm:flex"
                aria-label="Scroll to next section"
              >
                <ArrowDown className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>

          <div className="flex justify-center animate-reveal-up" style={{ animationDelay: "0.4s" }}>
            <img src={sphereImage} alt="Holographic 3D Sphere" className="w-full max-w-md animate-spin-glow" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;